import React, { useState, memo } from "react";
import useMapDevTools from "../../hooks/useMapDevTools";
import { useApp } from "../../../../AppProvider";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { IconButton, Tooltip, Typography } from "@mui/material";
import { Close, Code } from "@mui/icons-material";

const useClipboard = () => {
  const { doToast } = useApp();
  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        doToast("success", `Copied to clipboard: ${text}`);
      })
      .catch((err) => {
        doToast("error", `Error copying text: ${err}`);
      });
  };
  return copyToClipboard;
};

const formatCoordinate = (coord) =>
  ![undefined, null].includes(coord) ? coord.toFixed(5) : "N/A";

const RootContainer = styled(Paper)`
  position: absolute;
  width: 175px;
  padding: 12px;
  bottom: 30px;
  right: 50px;
  z-index: 1499;
`;

const ToggleButtonContainer = styled(Paper)`
  position: absolute;
  bottom: 30px;
  right: 50px;
  background-color: #ffffff;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 4px;
  right: 4px;
`;

const Header = styled(Typography)`
  font-weight: bold;
  margin-bottom: 8px;
`;

const CopyableText = memo(({ value, tooltipTitle, onCopy, ...props }) => {
  const handleClick = () => {
    if (onCopy) {
      onCopy(value);
    }
  };

  return (
    <Tooltip title={tooltipTitle || (onCopy ? "Click to copy" : "")}>
      <Typography
        {...props}
        onClick={handleClick}
        sx={{ cursor: onCopy ? "copy" : "inherit" }}
      />
    </Tooltip>
  );
});

const CoordinateGroup = memo(({ title, coordinates, onCopy }) => (
  <>
    <Typography variant="body1">
      <strong>{title}</strong>
    </Typography>
    {coordinates.map((coord, index) => (
      <CopyableText
        key={index}
        variant="body1"
        pl="16px"
        value={formatCoordinate(coord)}
        tooltipTitle={onCopy ? `Click to copy ${["lon", "lat"][index]}` : ""}
        onCopy={onCopy}
      >
        {`${["lon", "lat"][index]}: ${formatCoordinate(coord)}`}
      </CopyableText>
    ))}
  </>
));

const ToggleButton = memo(({ handleToggleVisibility }) => (
  <ToggleButtonContainer onClick={handleToggleVisibility}>
    <Tooltip title="Open Map Developer Tools">
      <IconButton size="small" color="default">
        <Code />
      </IconButton>
    </Tooltip>
  </ToggleButtonContainer>
));

const MapDevTools = ({ map }) => {
  const [isVisible, setIsVisible] = useState(false);

  const { zoomLevel, centerCoordinates, mouseCoordinates, mapBounds } =
    useMapDevTools(map, isVisible);
  const copyToClipboard = useClipboard();

  const handleToggleVisibility = () => setIsVisible(!isVisible);

  return (
    <>
      {isVisible ? (
        <RootContainer>
          <Header variant="body2">Map Dev Tools</Header>
          <CloseButton size="small" onClick={handleToggleVisibility}>
            <Close fontSize="small" />
          </CloseButton>
          <CopyableText
            value={zoomLevel.toFixed(1)}
            tooltipTitle="Click to copy zoom level"
            variant="body1"
            onCopy={copyToClipboard}
          >
            <strong>Zoom Level:</strong> {zoomLevel.toFixed(1)}
          </CopyableText>
          <CoordinateGroup
            title="Center Coords:"
            coordinates={centerCoordinates}
            onCopy={copyToClipboard}
          />
          <CoordinateGroup
            title="Mouse Coords:"
            coordinates={mouseCoordinates}
          />
          <CoordinateGroup
            title="SW Bounds:"
            coordinates={mapBounds[0]}
            onCopy={copyToClipboard}
          />
          <CoordinateGroup
            title="NE Bounds:"
            coordinates={mapBounds[1]}
            onCopy={copyToClipboard}
          />
        </RootContainer>
      ) : (
        <ToggleButton handleToggleVisibility={handleToggleVisibility} />
      )}
    </>
  );
};

export default memo(MapDevTools);
